import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Landmark, CornerDownRight } from "lucide-react";

export const StructureCard = ({ companyId, structure, companies, onChanged }) => {
  const [form, setForm] = useState({ parent: "none", pct: "100", book: "", acc: "1480" });
  const otherCompanies = (companies || []).filter((c) => (c._id || c.id) !== companyId);

  useEffect(() => {
    const me = structure?.company;
    setForm({
      parent: structure?.parent?.id || "none",
      pct: me?.participation_pct != null ? String(me.participation_pct) : "100",
      book: me?.participation_book_value != null ? String(me.participation_book_value) : "",
      acc: me?.participation_account_id || "1480",
    });
  }, [structure]);

  const save = async () => {
    try {
      await apiClient.put(`/companies/${companyId}/holding/parent`, {
        parent_company_id: form.parent === "none" ? null : form.parent,
        participation_pct: parseFloat(form.pct || "100"),
        participation_book_value: form.book === "" ? null : parseFloat(form.book),
        participation_account_id: form.acc || "1480",
      });
      toast.success(form.parent === "none" ? "Muttergesellschaft entfernt" : "Konzernstruktur gespeichert");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const rootId = structure?.root_id;
  const depth = (node, map, d = 0) => (node.parent_company_id && map[node.parent_company_id] ? depth(map[node.parent_company_id], map, d + 1) : d);
  const map = Object.fromEntries((structure?.group || []).map((g) => [g.id, g]));

  return (
    <Card className="p-4" data-testid="holding-structure-card">
      <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Building2 className="h-4 w-4" /> Konzernstruktur
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Muttergesellschaft dieser Firma</Label>
            <Select value={form.parent} onValueChange={(v) => setForm({ ...form, parent: v })}>
              <SelectTrigger data-testid="holding-parent-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Keine (eigenständig / Holding) —</SelectItem>
                {otherCompanies.map((c) => (
                  <SelectItem key={c._id || c.id} value={c._id || c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.parent !== "none" && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-slate-500">Beteiligung %</Label>
                <Input type="number" min="0" max="100" step="0.01" value={form.pct}
                  onChange={(e) => setForm({ ...form, pct: e.target.value })} data-testid="holding-pct-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Buchwert Beteiligung</Label>
                <Input type="number" step="0.05" placeholder="aus Konto" value={form.book}
                  onChange={(e) => setForm({ ...form, book: e.target.value })} data-testid="holding-bookvalue-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Konto bei Mutter</Label>
                <Input value={form.acc} onChange={(e) => setForm({ ...form, acc: e.target.value })}
                  data-testid="holding-partacc-input" />
              </div>
            </div>
          )}
          <Button size="sm" onClick={save} data-testid="holding-structure-save-btn">Speichern</Button>
          <p className="text-[11px] text-slate-400">
            Beteiligung &lt; 100 % → Minderheitsanteile. Buchwert &gt; anteiliges Eigenkapital → Goodwill (Konto 1490),
            darunter → Konsolidierungsreserve (Badwill).
          </p>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Konzernbaum ({structure?.group?.length || 0} Firmen)</Label>
          <div className="mt-1 space-y-1" data-testid="holding-group-tree">
            {(structure?.group || []).map((g) => {
              const d = depth(g, map);
              return (
                <div key={g.id} className="flex items-center gap-2 text-sm" style={{ paddingLeft: d * 18 }}
                  data-testid={`holding-tree-node-${g.id}`}>
                  {d > 0 ? <CornerDownRight className="h-3.5 w-3.5 text-slate-400" /> : <Landmark className="h-3.5 w-3.5 text-violet-600" />}
                  <span className={g.id === companyId ? "font-semibold text-slate-900" : "text-slate-700"}>{g.name}</span>
                  {g.id === rootId && <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700">Holding</Badge>}
                  {d > 0 && <span className="text-xs text-slate-400">{g.participation_pct ?? 100} %</span>}
                </div>
              );
            })}
          </div>
          {structure?.children?.length ? (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="holding-children-list">
              {structure.children.map((c) => (
                <Badge key={c.id} variant="outline" className="border-violet-300 text-violet-700">
                  <Landmark className="h-3 w-3 mr-1" /> {c.name}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-400 mt-3">Keine direkten Töchter — setze bei einer anderen Firma diese Firma als Mutter.</div>
          )}
        </div>
      </div>
    </Card>
  );
};
